"use client";
import type { SelectedStore } from "@components/try-and-buy/types/store";
import type { Product, ProductData, ProductVariant } from "@graphql/generated/types";
import type { PropsWithChildren } from "react";
import { createContext, useContext, useEffect, useState } from "react";

type TryAndBuyContextModel = {
    product: Product;
    masterVariant: ProductVariant;
    variants: ProductVariant[];
    currentMasterData: ProductData;
    sku: string | null;
    selectedStore: SelectedStore | null;
    setSelectedStore: (store: SelectedStore) => void;
    setSku: (sku: string) => void;
    isSuccess: boolean;
    setIsSuccess: (isSuccess: boolean) => void;
};

type Props = {
    product: Product;
    selectedVariant?: ProductVariant;
    isOpen: boolean;
} & PropsWithChildren;

const TryAndBuyContext = createContext<TryAndBuyContextModel | undefined>(undefined);

export const ContextProvider = ({ children, product, selectedVariant, isOpen }: Props) => {
    const [sku, setSku] = useState(null);
    const [selectedStore, setSelectedStore] = useState(null);
    const [isSuccess, setIsSuccess] = useState(false);

    const currentMasterData = product.masterData.current;
    const masterVariant = currentMasterData.masterVariant;
    const variants = currentMasterData?.variants ?? [];

    useEffect(() => {
        if (!selectedVariant) return;
        setSku(selectedVariant.sku);
    }, [selectedVariant]);

    useEffect(() => {
        if (isOpen) return;
        setSelectedStore(null);
        setSku(selectedVariant?.sku ?? null);
        setIsSuccess(false);
    }, [isOpen, selectedVariant]);

    return (
        <TryAndBuyContext.Provider
            value={{
                product,
                isSuccess,
                setIsSuccess,
                masterVariant,
                variants,
                currentMasterData,
                sku,
                selectedStore,
                setSelectedStore,
                setSku,
            }}
        >
            {children}
        </TryAndBuyContext.Provider>
    );
};

export const useTryAndBuyContext = () => useContext(TryAndBuyContext);
